/**
 * Retning E: bevegelsen, og den ENESTE kilden til den.
 *
 * Sekvensen, i rekkefølge:
 *
 *   1. Dråpen tones inn der den svever, på skrå over flaten.
 *   2. Den faller, og den akselererer, fordi den faller.
 *   3. Den treffer og klemmes flat. Nedslaget er det bølgene starter fra.
 *   4. Tre bølger går utover i perspektiv og dør før de når kanten.
 *   5. Ringene faller på plass etter hverandre, med et lite oversving.
 *   6. Kameraet vippes opp. Ellipsene retter seg ut til sirkler, flaten
 *      reiser seg til midten, og dråpen ender som prikken i sentrum.
 *   7. Det som blir stående er logoen, sett rett ovenfra.
 *
 * `frameAt()` er en ren funksjon av tid. Både React-komponenten og
 * bildeserien som lager GIF-en tegner fra den, så det som vises i en
 * forhåndsvisning ER det som kjører i appen.
 *
 * Siste bilde, `frameAt(DURATION)`, er nøyaktig hviletilstanden: tilt 1,
 * grow 1, bølgene borte, dråpen i sentrum. Derfor kan animasjonen spilles
 * når som helst uten at noe hopper når den er ferdig.
 */
import { CENTER, ringPath, ryFor, surfaceCy, WAVE_RX, WAVE_W, type CutSpec } from './ripple-geometry'

/** Hele sekvensen, fra dråpen tones inn til siste ring står stille. */
export const DURATION = 2600

const HOVER_IN = 140
const FALL_START = 220
const IMPACT = 620
const WAVE_FIRST = 600
const WAVE_STEP = 250
const WAVE_LIFE = 950
const RING_FIRST = 760
const RING_STEP = 220
const RING_LIFE = 640
const TILT_START = 1150
const TILT_END = 2200

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const easeInQuad = (t: number) => t * t
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

/** Oversving: forbi 1, og så tilbake til nøyaktig 1. */
function settle(t: number): number {
  const e = easeOutCubic(t)
  return 0.45 + (1 - 0.45) * e + Math.sin(Math.PI * t) * 0.07 * (1 - t)
}

export interface RingState {
  grow: number
  o: number
}
export interface WaveState {
  rx: number
  o: number
}
export interface Frame {
  tilt: number
  /** Dråpens senter og klem. sx/sy er 1 når den er rund. */
  drop: { cy: number; sx: number; sy: number; o: number }
  rings: RingState[]
  waves: WaveState[]
}

export function frameAt(ms: number, cut: CutSpec): Frame {
  const t = clamp01(ms / DURATION) * DURATION

  const tilt = easeInOutCubic(clamp01((t - TILT_START) / (TILT_END - TILT_START)))
  const surface = surfaceCy(tilt)

  // ── dråpen ──────────────────────────────────────────────────────────────
  let cy: number
  let sx = 1
  let sy = 1
  const fadeIn = clamp01(t / HOVER_IN)

  if (t < FALL_START) {
    cy = cut.drop.hoverCy
  } else if (t < IMPACT) {
    const p = easeInQuad(clamp01((t - FALL_START) / (IMPACT - FALL_START)))
    cy = cut.drop.hoverCy + (surfaceCy(0) - cut.drop.hoverCy) * p
    // strekkes litt i fallet, slik en dråpe faktisk gjør
    sy = 1 + 0.18 * p
    sx = 1 - 0.12 * p
  } else {
    // etter nedslaget rir dråpen med flaten opp til midten
    cy = surface
    const since = t - IMPACT
    if (since < 260) {
      // klemmes flat, spretter tilbake
      const p = since / 260
      const squash = Math.sin(Math.PI * p) * (1 - p)
      sy = 1 - 0.42 * squash
      sx = 1 + 0.34 * squash
    }
  }

  // ── bølgene ─────────────────────────────────────────────────────────────
  const waves: WaveState[] = []
  for (let i = 0; i < cut.waves; i++) {
    const start = WAVE_FIRST + i * WAVE_STEP
    const p = clamp01((t - start) / WAVE_LIFE)
    if (p <= 0 || p >= 1) {
      waves.push({ rx: 2, o: 0 })
      continue
    }
    const e = easeOutCubic(p)
    // full styrke tidlig, borte lenge før ytterkanten
    const o = p < 0.18 ? (p / 0.18) * 0.8 : 0.8 * Math.pow(1 - (p - 0.18) / 0.82, 1.6)
    waves.push({ rx: 2 + (WAVE_RX - 2) * e, o })
  }

  // ── ringene ─────────────────────────────────────────────────────────────
  const rings: RingState[] = cut.rings.map((ring, i) => {
    const start = RING_FIRST + i * RING_STEP
    const p = clamp01((t - start) / RING_LIFE)
    if (p <= 0) return { grow: 0.45, o: 0 }
    return { grow: settle(p), o: ring.o * clamp01(p / 0.35) }
  })

  return { tilt, drop: { cy, sx, sy, o: fadeIn }, rings, waves }
}

/**
 * Ett bilde som SVG-innmat. Brukes av bildeserien som lager forhåndsvisninger.
 * Komponenten setter attributtene direkte i stedet, men regner ut det samme
 * fra `frameAt`, så de kan ikke vise forskjellige ting.
 */
export function frameMarkup(frame: Frame, cut: CutSpec, color: string): string {
  const surface = surfaceCy(frame.tilt)

  const waves = frame.waves
    .map((w) =>
      w.o <= 0.001
        ? ''
        : `<ellipse cx="${CENTER}" cy="${surface.toFixed(2)}" rx="${w.rx.toFixed(2)}" ry="${ryFor(
            w.rx,
            frame.tilt
          ).toFixed(2)}" fill="none" stroke="${color}" stroke-width="${WAVE_W}" opacity="${w.o.toFixed(3)}"/>`
    )
    .join('')

  const rings = cut.rings
    .map((ring, i) => {
      const s = frame.rings[i]
      return s.o <= 0.001
        ? ''
        : `<path d="${ringPath(ring, frame.tilt, s.grow)}" fill="${color}" opacity="${s.o.toFixed(3)}"/>`
    })
    .join('')

  const d = frame.drop
  const drop =
    `<g transform="translate(${CENTER} ${d.cy.toFixed(2)}) scale(${d.sx.toFixed(3)} ${d.sy.toFixed(3)})">` +
    `<circle cx="0" cy="0" r="${cut.drop.r}" fill="${color}" opacity="${d.o.toFixed(3)}"/></g>`

  return waves + rings + drop
}
